import {useNavigate, useSearchParams} from "react-router-dom";
import {ttaSelector, useGetTtaByPickupAndDeliveryQuery, useGetTtaQuery} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {useEffect, useMemo, useState} from "react";
import {ErrorPage} from "../../components/page/ErrorPage/ErrorPage";
import {useTranslation} from "react-i18next";
import {TicketMap} from "../../components/profile/TicketProfile/TicketMap";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";

type Props = {
  id?: string;
  pickupId?: string
  deliveryId?: string
  database?: string
}

export const TicketLocationHistoryMapPageRoute = () => {
  const [params, _setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const id = params.get("id") ?? undefined;
  const pickupId = params.get("pid") ?? undefined
  const deliveryId = params.get("did") ?? undefined
  const database = params.get("database") ?? undefined;

  const {t} = useTranslation(["common"])

  if (!id && (!pickupId || !deliveryId)) {
    navigate(-1);
    return (
      <ErrorPage
        message={t("common.entity.ticket", "Ticket") + " " + t("common.error.notfound", "Not Found")}/>
    );
  }

  return <TicketLocationHistoryMapPage id={id} pickupId={pickupId} deliveryId={deliveryId} database={database}/>;
};

export const TicketLocationHistoryMapPage = (props: Props) => {
  const {id, pickupId, deliveryId, database} = props;

  const {t} = useTranslation(["common"])

  const [access, setAccess] = useState(false);
  const [loading, setLoading] = useState(false)

  const selectTicket = useMemo(() => {
    return ttaSelector()
  }, []);

  const idQueryArg = useMemo(() => {
    return access ? (id ? {id: id, database: database} : skipToken) : skipToken
  }, [access, id, database])

  const {idTicket, isIdFetching} = useGetTtaQuery(idQueryArg, {
    selectFromResult: (result) => ({
      idTicket: selectTicket(result),
      isIdFetching: result.isFetching
    })
  });

  const pickupDeliveryQueryArg = useMemo(() => {
    if (access && !id && pickupId && deliveryId) {
      return {
        pickupId: pickupId,
        deliveryId: deliveryId,
        database: database
      }
    } else {
      return skipToken
    }
  }, [access, id, pickupId, deliveryId, database])

  const {pdTicket, isPDFetching} = useGetTtaByPickupAndDeliveryQuery(pickupDeliveryQueryArg, {
    selectFromResult: (result) => ({
      pdTicket: selectTicket(result),
      isPDFetching: result.isFetching
    })
  });

  useEffect(() => {
    setLoading(isIdFetching || isPDFetching)
  }, [isIdFetching, isPDFetching]);

  const ticket = idTicket ?? pdTicket

  const error = !ticket ? t<string>("common.error.notfound", "Not Found") : undefined

  return (
    <ProtectedPage
      permissions={["transporttransportassoc_view"]}
      isLoading={loading}
      error={error}
      onAccess={setAccess}>
      <TicketMap ticket={ticket}/>
    </ProtectedPage>
  );
};
